import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight } from "lucide-react";
import { BuilderWordmark } from "@/components/BuilderWordmark";

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const safeReturn =
    typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//")
      ? returnTo
      : null;
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        toast({
          title: "Konto utworzone",
          description: "Sprawdź email, aby potwierdzić rejestrację.",
        });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate(safeReturn ?? "/");
      }
    } catch (error: any) {
      toast({
        title: "Błąd",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Nie udało się zalogować przez Google.";
      toast({
        title: "Błąd",
        description: message,
        variant: "destructive",
      });
      setOauthLoading(false);
    }
  };

  const busy = loading || oauthLoading;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "var(--color-bg-page)", color: "var(--color-text-primary)" }}
    >
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <h1 style={{ fontSize: "1.5rem", margin: 0, marginBottom: "0.25rem" }}>
            <BuilderWordmark theme="light" />
          </h1>
          <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            Profesjonalne wyceny w 30 sekund
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="mb-6 h-12 w-full gap-3 border-2 border-border bg-background font-medium"
          style={{
            background: "var(--color-bg-surface)",
            borderColor: "var(--color-border-emphasis)",
            color: "var(--color-text-primary)",
          }}
          disabled={busy}
          onClick={() => void handleGoogleLogin()}
        >
          <GoogleMark className="h-5 w-5 shrink-0" />
          {oauthLoading ? "Przekierowywanie…" : "Zaloguj przez Google"}
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span
              className="px-3"
              style={{ background: "var(--color-bg-page)", color: "var(--color-text-muted)" }}
            >
              lub e-mail
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 border-2"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Hasło</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="h-12 border-2"
            />
          </div>
          <Button type="submit" variant="action" size="lg" className="w-full" disabled={busy}>
            {loading ? "Ładowanie..." : isSignUp ? "Załóż konto" : "Zaloguj się"}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </form>

        <button
          type="button"
          onClick={() => setIsSignUp(!isSignUp)}
          className="mt-4 text-sm w-full text-center"
          style={{ color: "var(--color-text-secondary)" }}
          disabled={busy}
        >
          {isSignUp ? "Masz konto? Zaloguj się" : "Nie masz konta? Zarejestruj się"}
        </button>
      </div>
    </div>
  );
}
